/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import { Stack, Panel, PanelType, Checkbox } from "@fluentui/react";
import { ControlType, Levels } from "@nuance/hierarchical-settings-ui-domain";
import { IPanelFooterProps } from "./IPanelFooterProps";
import { IHelpTextChangeData } from ".";
import HelpText from "./HelpText";
import Audit from "./Audit";
import { HuxTextBox } from "@nuance/hux-components";
import AccessLevelSelection from "./AccessLevelSelection";
import { panelProps } from "./FieldDefinitionPanelStackTokens";
import PanelFooter from "./PanelFooter";

/**
 * Props for FieldDefinitionPanel
 */
export interface FieldDefinitionPanelProps {
    children: React.ReactNode;

    title: string;
    isOpen: boolean;
    onClosePanel?: () => void;
    footerProps: IPanelFooterProps;

    settingName: string;
    settingNameErrorMessage?: string;
    onChangeSettingName: (name: string, value: string | undefined) => void;
    onBlurSettingName: (targetName: string) => void;
    settingNameReadOnly: boolean;

    titleValue: string;
    titleErrorMessage?: string;
    onChangeTitle: (name: string, value: string | undefined) => void;
    onBlurTitle: (targetName: string) => void;

    showRequired?: boolean;
    isRequired?: boolean;
    onChangeRequired?: () => void;

    tooltipText: string;
    helperText: string;
    onHelpTextChange: (data: IHelpTextChangeData) => void;

    accessLevel: Levels;
    onAccessLevelChange?: (newAccessLevel: Levels) => void;

    auditChanges: boolean;
    onAuditChangesChanged: (checked: boolean) => void;

    controlType: ControlType;
}

const FieldDefinitionPanel = (props: FieldDefinitionPanelProps) => {
    return (
        <Panel
            isOpen={true}
            onDismiss={props.onClosePanel}
            headerText={props.title}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={() => {
                return PanelFooter(props.footerProps);
            }}
            // Stretch panel content to fill the available height so
            // the footer is positioned at the bottom of the page
            isFooterAtBottom={true}
            type={PanelType.medium}
        >
            <Stack {...panelProps}>
                <HuxTextBox
                    required
                    label="Setting Name"
                    name="settingName"
                    value={props.settingName}
                    errorMessage={props.settingNameErrorMessage}
                    onChange={(e, value) => props.onChangeSettingName(e.currentTarget.name, value)}
                    onBlur={e => props.onBlurSettingName(e.currentTarget.name)}
                    disabled={props.settingNameReadOnly}
                />
                <HuxTextBox
                    required
                    label="Label"
                    name="title"
                    value={props.titleValue}
                    errorMessage={props.titleErrorMessage}
                    onChange={(e, value) => props.onChangeTitle(e.currentTarget.name, value)}
                    onBlur={e => props.onBlurTitle(e.currentTarget.name)}
                />

                {props.showRequired && (
                    <Checkbox
                        label="Is required"
                        checked={props.isRequired}
                        onChange={() => props.onChangeRequired?.()}
                    />
                )}

                <HelpText
                    tooltipText={props.tooltipText}
                    helperText={props.helperText}
                    onHelpTextChange={changeData => props.onHelpTextChange(changeData)}
                    controlType={props.controlType}
                />

                {props.children}

                <AccessLevelSelection
                    accessLevel={props.accessLevel}
                    onAccessLevelChange={props.onAccessLevelChange}
                />

                <Audit
                    auditChanges={props.auditChanges}
                    onAuditChangesChanged={props.onAuditChangesChanged}
                />
            </Stack>
        </Panel>
    );
};

export default FieldDefinitionPanel;
